// Adapter boundary: plain domain values → the swagger-generated nominal enums. Every
// dimension maps through a typed Record whose keys are the domain literal union and whose
// values are the target enum members — so the mapping is exhaustive and type-safe with no
// `as` assertions anywhere (the RushMid lint gate forbids them).
import {
  type RushMidLoadout,
  type CreateOpenGameRequest,
  type JoinOpenGameRequest,
  type PartyConfigRequest,
  type SubmitActionRequest,
  RushMidLoadoutWeaponEnum,
  RushMidLoadoutArmorEnum,
  CreateOpenGameRequestSideEnum,
  CreateOpenGameRequestMatchModeEnum,
  JoinOpenGameRequestSideEnum,
  PartyConfigRequestSideEnum,
  PartyConfigRequestMatchModeEnum,
  SubmitActionRequestKindEnum,
  SubmitActionRequestStanceEnum,
} from '../../../services/generated/Api';
import type { Action, ActionKind, Loadout, Stance, Team, WeaponClass, ArmorType } from '@cs-shot/battle-sim';
import type { SidePref } from './useRushMidMatch';

/** Team size 1/2/5 — shared by every request enum (Value1/Value2/Value5). */
export const modeEnum = (mode: number): 1 | 2 | 5 => (mode === 2 ? 2 : mode === 5 ? 5 : 1);

/** Entry ticket — shared by every request enum (Value100…Value100000). */
export const ticketEnum = (ticket: number): 100 | 500 | 1000 | 5000 | 25000 | 100000 =>
  ticket === 100 ? 100 : ticket === 500 ? 500 : ticket === 5000 ? 5000 : ticket === 25000 ? 25000 : ticket === 100000 ? 100000 : 1000;

const WEAPON_ENUM: Record<WeaponClass, RushMidLoadoutWeaponEnum> = {
  awp: RushMidLoadoutWeaponEnum.Awp,
  rifle: RushMidLoadoutWeaponEnum.Rifle,
  lmg: RushMidLoadoutWeaponEnum.Lmg,
};

const ARMOR_ENUM: Record<ArmorType, RushMidLoadoutArmorEnum> = {
  none: RushMidLoadoutArmorEnum.None,
  light: RushMidLoadoutArmorEnum.Light,
  heavy: RushMidLoadoutArmorEnum.Heavy,
};

const CREATE_SIDE_ENUM: Record<Team, CreateOpenGameRequestSideEnum> = {
  t: CreateOpenGameRequestSideEnum.T,
  ct: CreateOpenGameRequestSideEnum.Ct,
};

const JOIN_SIDE_ENUM: Record<Team, JoinOpenGameRequestSideEnum> = {
  t: JoinOpenGameRequestSideEnum.T,
  ct: JoinOpenGameRequestSideEnum.Ct,
};

const PARTY_SIDE_ENUM: Record<SidePref, PartyConfigRequestSideEnum> = {
  t: PartyConfigRequestSideEnum.T,
  ct: PartyConfigRequestSideEnum.Ct,
  any: PartyConfigRequestSideEnum.Any,
};

const CREATE_MATCH_MODE_ENUM: Record<'ranked' | 'bots', CreateOpenGameRequestMatchModeEnum> = {
  ranked: CreateOpenGameRequestMatchModeEnum.Ranked,
  bots: CreateOpenGameRequestMatchModeEnum.Bots,
};

const PARTY_MATCH_MODE_ENUM: Record<'ranked' | 'bots', PartyConfigRequestMatchModeEnum> = {
  ranked: PartyConfigRequestMatchModeEnum.Ranked,
  bots: PartyConfigRequestMatchModeEnum.Bots,
};

const ACTION_KIND_ENUM: Record<ActionKind, SubmitActionRequestKindEnum> = {
  shoot: SubmitActionRequestKindEnum.Shoot,
  knife: SubmitActionRequestKindEnum.Knife,
  grenade: SubmitActionRequestKindEnum.Grenade,
  medkit: SubmitActionRequestKindEnum.Medkit,
  smoke: SubmitActionRequestKindEnum.Smoke,
  flash: SubmitActionRequestKindEnum.Flash,
};

const STANCE_ENUM: Record<Stance, SubmitActionRequestStanceEnum> = {
  attack: SubmitActionRequestStanceEnum.Attack,
  defense: SubmitActionRequestStanceEnum.Defense,
  maneuver: SubmitActionRequestStanceEnum.Maneuver,
};

/** A concrete seated side — 'any' resolves to T (the creator sits on T by default). */
const concreteSide = (side: SidePref): Team => (side === 'ct' ? 'ct' : 't');

export const toApiLoadout = (l: Loadout): RushMidLoadout => ({
  weapon: WEAPON_ENUM[l.weapon],
  armor: ARMOR_ENUM[l.armor],
  grenade: l.grenade,
  smoke: l.smoke,
  medkit: l.medkit,
  flash: l.flash,
});

/** Create-open-game request — the creator takes one concrete side (never 'any'). */
export const toCreateGameRequest = (
  mode: number,
  ticket: number,
  side: SidePref,
  matchMode?: 'ranked' | 'bots',
): CreateOpenGameRequest => ({
  mode: modeEnum(mode),
  ticket: ticketEnum(ticket),
  side: CREATE_SIDE_ENUM[concreteSide(side)],
  ...(matchMode ? { matchMode: CREATE_MATCH_MODE_ENUM[matchMode] } : {}),
});

/** Join-open-game request — the joiner picks a concrete side that has room. */
export const toJoinGameRequest = (side: Team): JoinOpenGameRequest => ({
  side: JOIN_SIDE_ENUM[side],
});

/** Party-config request (leader sets mode/ticket/side/matchMode). */
export const toPartyConfig = (
  mode: number,
  ticket: number,
  side: SidePref,
  matchMode?: 'ranked' | 'bots',
): PartyConfigRequest => ({
  mode: modeEnum(mode),
  ticket: ticketEnum(ticket),
  side: PARTY_SIDE_ENUM[side],
  ...(matchMode ? { matchMode: PARTY_MATCH_MODE_ENUM[matchMode] } : {}),
});

/** Round action → wire body — the ONE place the domain Action is shaped for the API, so a
 *  new field/kind is added here, not silently dropped from a hand-rolled copy in the hook. */
export const toApiAction = (action: Action, round?: number): SubmitActionRequest => {
  const body: SubmitActionRequest = { kind: ACTION_KIND_ENUM[action.kind] };
  if (round !== undefined) body.round = round;
  if (action.stance) body.stance = STANCE_ENUM[action.stance];
  if (action.shootTargetId) body.shootTargetId = action.shootTargetId;
  if (action.grenadeTargetId) body.grenadeTargetId = action.grenadeTargetId;
  if (action.smokeTargetId) body.smokeTargetId = action.smokeTargetId;
  if (action.flashTargetId) body.flashTargetId = action.flashTargetId;
  return body;
};
